/* ==========================================================================
   Modal Controller
   Web Component: <modal-component>
   Hydration tier: 'interaction' (external trigger pattern)

   Generic fullscreen lightbox container. Accepts any content by default
   (type: 'custom') and provides convenience presets for media types
   (image, video, youtube, vimeo, html). Includes custom Instagram-style
   video controls for hosted video playback.
   ========================================================================== */

import { BaseComponent as RkComponent } from '@theme/base-component';

function waitForExitAnimation(el, done) {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!el || reduce) {
        done();
        return () => {};
    }

    let settled = false;
    const onEnd = (e) => {
        if (e.target !== el) return;
        finish();
    };
    const finish = () => {
        if (settled) return;
        settled = true;
        el.removeEventListener('transitionend', onEnd);
        clearTimeout(timer);
        done();
    };
    const cancel = () => {
        if (settled) return;
        settled = true;
        el.removeEventListener('transitionend', onEnd);
        clearTimeout(timer);
    };
    el.addEventListener('transitionend', onEnd);
    const timer = setTimeout(finish, 500);
    return cancel;
}

class RkModalElement extends RkComponent {
    static hydration = 'interaction';

    #raf = null;
    #cancelExitAnim = null;
    #keydownController = null;
    #triggerCleanups = [];

    attachHydrationTrigger(hydrate) {
        const modal = this.querySelector('.modal');
        const id = this.dataset.modalId || modal?.id;
        if (!id) return () => {};

        const triggers = document.querySelectorAll(`[data-modal-open="${id}"]`);
        const cleanups = [];

        triggers.forEach((trigger) => {
            const onClick = (event) => {
                event.preventDefault();
                trigger.removeEventListener('click', onClick);
                hydrate();
                this.open();
            };
            trigger.addEventListener('click', onClick);
            cleanups.push(() => trigger.removeEventListener('click', onClick));
        });

        return () => cleanups.forEach((fn) => fn());
    }

    setup() {
        this.modal = this.querySelector('.modal');
        this.content = this.querySelector('.modal__content');
        this.previousFocus = null;

        if (!this.modal) return;

        this.delegate({
            'click [data-action="close"]': () => this.close(),
        });

        this.#setupPersistentOpenTriggers();
        this.#setupVideoControls();
    }

    teardown() {
        if (this.#raf) {
            cancelAnimationFrame(this.#raf);
            this.#raf = null;
        }
        if (this.#cancelExitAnim) {
            this.#cancelExitAnim();
            this.#cancelExitAnim = null;
        }
        if (this.#keydownController) {
            this.#keydownController.abort();
            this.#keydownController = null;
        }
        this.#triggerCleanups.forEach((fn) => fn());
        this.#triggerCleanups = [];
    }

    #setupPersistentOpenTriggers() {
        const modalId = this.dataset.modalId || this.modal?.id;
        if (!modalId) return;

        const triggers = document.querySelectorAll(`[data-modal-open="${modalId}"]`);
        triggers.forEach((trigger) => {
            const onClick = (e) => {
                e.preventDefault();
                this.open();
            };
            trigger.addEventListener('click', onClick);
            this.#triggerCleanups.push(() => trigger.removeEventListener('click', onClick));
        });
    }

    #setupVideoControls() {
        const video = this.querySelector('.modal__video');
        if (!video) return;

        this._video = video;
        this._controls = this.querySelector('.modal__controls');
        this._progressBar = this.querySelector('.modal__progress-bar');
        this._progressTrack = this.querySelector('[data-ctrl="progress"]');
        this._timeDisplay = this.querySelector('[data-ctrl="time"]');
        this._playPauseBtn = this.querySelector('[data-ctrl="play-pause"]');
        this._muteBtn = this.querySelector('[data-ctrl="mute"]');
        this._tapOverlay = this.querySelector('[data-ctrl="tap"]');

        this.delegate({
            'click [data-ctrl="play-pause"]': (e) => { e.stopPropagation(); this.#togglePlay(); },
            'click [data-ctrl="mute"]': (e) => { e.stopPropagation(); this.#toggleMute(); },
            'click [data-ctrl="tap"]': (e) => { e.stopPropagation(); this.#togglePlay(); },
            'click [data-ctrl="progress"]': (e) => { e.stopPropagation(); this.#seek(e); },
        });

        video.addEventListener('play', () => this.#syncPlayState());
        video.addEventListener('pause', () => this.#syncPlayState());
        video.addEventListener('volumechange', () => this.#syncMuteState());
    }

    #togglePlay() {
        if (!this._video) return;
        if (this._video.paused) {
            this._video.play().catch(() => {});
        } else {
            this._video.pause();
        }
    }

    #toggleMute() {
        if (!this._video) return;
        this._video.muted = !this._video.muted;
    }

    #seek(e) {
        if (!this._video || !this._progressTrack) return;
        const rect = this._progressTrack.getBoundingClientRect();
        const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
        this._video.currentTime = ratio * this._video.duration;
    }

    #syncPlayState() {
        if (!this._video || !this._controls) return;
        const playing = !this._video.paused;
        this._controls.classList.toggle('is-playing', playing);
        this._controls.classList.toggle('is-paused', !playing);

        if (playing) {
            this.#startProgressLoop();
        } else {
            this.#stopProgressLoop();
        }
    }

    #syncMuteState() {
        if (!this._video || !this._controls) return;
        this._controls.classList.toggle('is-muted', this._video.muted);
        this._controls.classList.toggle('is-unmuted', !this._video.muted);
    }

    #startProgressLoop() {
        const update = () => {
            if (!this._video || this._video.paused) return;

            const pct = (this._video.currentTime / this._video.duration) * 100 || 0;
            if (this._progressBar) {
                this._progressBar.style.width = pct + '%';
            }

            if (this._timeDisplay) {
                this._timeDisplay.textContent = this.#formatTime(this._video.currentTime);
            }

            this.#raf = requestAnimationFrame(update);
        };
        this.#raf = requestAnimationFrame(update);
    }

    #stopProgressLoop() {
        if (this.#raf) {
            cancelAnimationFrame(this.#raf);
            this.#raf = null;
        }
    }

    #formatTime(seconds) {
        const m = Math.floor(seconds / 60);
        const s = Math.floor(seconds % 60);
        return m + ':' + (s < 10 ? '0' : '') + s;
    }

    isOpen() {
        return this.modal?.classList.contains('modal--active');
    }

    open() {
        if (!this.modal) return;

        if (this.#cancelExitAnim) {
            this.#cancelExitAnim();
            this.#cancelExitAnim = null;
        }

        if (this.isOpen() && this.modal.dataset.state !== 'closed') return;

        this.previousFocus = document.activeElement;
        this.modal.classList.add('modal--active');
        this.modal.dataset.state = 'open';
        document.body.style.overflow = 'hidden';

        this.#keydownController?.abort();
        this.#keydownController = new AbortController();
        document.addEventListener('keydown', (e) => this.#onKeyDown(e), { signal: this.#keydownController.signal });

        this.#loadIframes();
        this.#playMedia();

        if (this._video) {
            this.#syncPlayState();
            this.#syncMuteState();
        }

        requestAnimationFrame(() => {
            const closeBtn = this.querySelector('.modal__close');
            if (closeBtn) closeBtn.focus();
        });

        this.emit('modal:open', { id: this.modal.id, type: this.dataset.type });
    }

    close() {
        if (!this.modal || !this.isOpen()) return;
        if (this.modal.dataset.state === 'closed') return;

        this.#stopMedia();
        this.#stopProgressLoop();

        this.modal.dataset.state = 'closed';

        const finalize = () => {
            this.#cancelExitAnim = null;
            this.modal.classList.remove('modal--active');
            delete this.modal.dataset.state;
            document.body.style.overflow = '';

            this.#keydownController?.abort();
            this.#keydownController = null;

            if (this.previousFocus) {
                this.previousFocus.focus();
                this.previousFocus = null;
            }

            this.emit('modal:close', { id: this.modal.id, type: this.dataset.type });
        };

        this.#cancelExitAnim = waitForExitAnimation(this.content, finalize);
    }

    #loadIframes() {
        this.querySelectorAll('iframe[data-src]').forEach((iframe) => {
            if (!iframe.src || iframe.src === 'about:blank') {
                iframe.src = iframe.dataset.src;
            }
        });
    }

    #playMedia() {
        const video = this.querySelector('video[data-autoplay]');
        if (video) {
            video.muted = true;
            video.currentTime = 0;
            video.play().catch(() => {});
        }
    }

    #stopMedia() {
        this.querySelectorAll('video').forEach((v) => {
            v.pause();
            v.currentTime = 0;
        });

        this.querySelectorAll('iframe').forEach((iframe) => {
            if (iframe.dataset.src) {
                iframe.src = 'about:blank';
            }
        });
    }

    #onKeyDown(e) {
        if (!this.isOpen()) return;

        if (e.key === 'Escape') {
            e.preventDefault();
            this.close();
            return;
        }

        if (e.key === ' ' && this._video) {
            e.preventDefault();
            this.#togglePlay();
            return;
        }

        if (e.key === 'm' && this._video) {
            e.preventDefault();
            this.#toggleMute();
        }
    }
}

if (!customElements.get('modal-component')) {
    customElements.define('modal-component', RkModalElement);
}
